import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    bulkResetAccountPasswords,
    deleteAccount,
    issueAccount,
    resetAccountPassword,
    updateAccountEnabled,
    type AccountIssued,
    type AccountPasswordReset,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { Button, Callout, Drawer, FormField, Input, Modal, OptionCard, SkeletonTable, useToast } from '@/components/ui';
import { AccountsScreen } from './Accounts/Shared/AccountsScreen';
import { AccountsBulkBar, type AccountsBulkAction } from './Accounts/Shared/AccountsBulkBar';
import { chunk } from './Accounts/chunk';
import { AccountsPagination } from './Accounts/Shared/AccountsPagination';
import {
    AccountsListTable,
    AccountsEmptyNew,
    AccountsEmptyFiltered,
    BulkDeleteConfirmModal,
    DeleteAccountDialog,
    LockAccountDialog,
    ResetAccountDrawer,
    ResetCredentialFields,
    useResetCredential,
    type ResetCredentialMode,
    BulkResetResultPanel,
    type BulkResetResultRow,
    OneTimeSecretModal,
    useRealmAccountsAdapter,
    useAssignLevelToAccount,
    AssignLevelToAccountDialog,
    useDeactivateAccount,
    DeactivateAccountDialog,
    useBulkAssignAccountsToOrg,
    BulkAssignAccountsToOrgDialog,
    BulkAssignOrgResultPanel,
    AccountDetailModal,
    useRealmAccountViews,
    type KeycloakAccountRow,
} from './Accounts';
import { AccountAuditPanel } from './Audit/AccountAuditPanel';
import { canListAccounts } from './Accounts/accountsAccess';

const EMPTY_FORM = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    credential_mode: 'temp_password' as 'invite' | 'temp_password',
};

const ACCOUNTS_TABLE_COLUMNS = 6;

/**
 * EAA-1820 (screen unification pass, ticket EAA-1820 reused — see the ticket
 * comment for why the same key covers both the original realm-screen build and
 * this later merge): `/console/accounts` now renders through the ONE shared
 * `AccountsScreen`, per the design pack's own governing rule quoted in the
 * ticket brief — realm and org account lists "dùng chung một UI: cùng tab, bộ
 * lọc đã lưu, rail bộ lọc, bảng và thanh thao tác. Khác nhau chỉ ở ngữ cảnh."
 *
 * REVERSAL FROM THE PREVIOUS DESIGN, recorded rather than silently dropped:
 * this screen and `ScopedAccountsPage.tsx` used to be two independent
 * implementations (EAA-1820 realm-only build, then EAA-1839 for the org
 * screen), each with its own facet rail, bulk bar, pagination and table
 * component, justified at the time by the two backends' very different
 * capabilities (a capped client-side Keycloak-realm walk vs. real server
 * pagination over `Assignment` rows). That justification for TWO components
 * stopped holding once `GET /api/v1/accounts` grew real server-side
 * pagination/search/filter/sort and a `/accounts/facets` aggregate endpoint
 * (this file used to drive `useAccountsScan`'s capped 2,000-row walk — DELETED,
 * not kept as a fallback, see `useRealmAccountsAdapter.ts`) — at that point the
 * only real difference between the two screens was the columns and the
 * write-flows, which is exactly what `shared/AccountsScreen` factors out.
 *
 * WHAT STILL DOES NOT EXIST, restated from the previous docstring so a later
 * session does not have to re-derive it:
 * 1. REVERSED 2026-08-21 (user decision), recorded rather than rewritten. This
 *    item used to read, in full: "Account drill-in is `/console/accounts/:sub`
 *    (`AccountProfilePage`), fed by `GET /accounts/<sub>`. `AccountDetailDrawer`
 *    is retired from the list flow." That was the frame-72 decision
 *    (SRS-Portal-Accounts-Frontend): the drill-in had been a drawer ON the list
 *    and was deliberately made a ROUTE, so a deep link to one account exists and
 *    the list is not carrying a second, half-populated copy of the profile. That
 *    reasoning was real and still holds for the "Xem" button, which navigates to
 *    the route exactly as before.
 *
 *    What changed: the user asked for a ROW CLICK to open a quick detail over the
 *    list instead of leaving it. So `AccountDetailDrawer` — which was dead code,
 *    rendered by nothing — is revived as `AccountDetailModal` (a kit `Modal`,
 *    renamed because a component called `...Drawer` that renders a modal is stale
 *    naming) rather than a third detail surface being written. The two paths
 *    COEXIST ON PURPOSE and go to different places: the kebab's "Xem chi tiết"
 *    opens the modal; the "Xem" button navigates to `/console/accounts/:sub`.
 *    Neither is a duplicate of the other, and the button remains the
 *    keyboard/screen-reader path (a `<tr>` is not focusable).
 *
 *    DONE (EAA-1839, 2026-08-24). This item used to read "STILL OUTSTANDING …
 *    until then a row click NAVIGATES": `AccountsTable` derived its row click from
 *    `onViewDetail`, the same callback the "Xem" button fires, so the two could
 *    not differ. It now takes an optional `onRowClick` used in place of
 *    `onViewDetail` inside `handleRowClick`, which this screen passes — so a row
 *    click opens the modal and "Xem" still navigates. The
 *    `ROW_CLICK_IGNORE_SELECTOR` guard needed no change (verified in the running
 *    app: the selection checkbox, the kebab and the "Xem" button each fail to
 *    open the modal).
 * 2. UPDATED by EAA-1820 (frame 86) — this used to say there was no enable/
 *    disable toggle, only the irreversible `DELETE`. `PATCH /accounts/{sub}
 *    {enabled}` exists now (`AccountDetailView.patch`) and is wired to the row
 *    kebab's "Vô hiệu hoá tài khoản"/"Kích hoạt lại" (`useDeactivateAccount`).
 *    `DELETE` (hard, irreversible) is unchanged and stays a separate item.
 * 3. No true last-login timestamp; `lastLoginAvailable` gates an explicit
 *    "Chưa khả dụng" vs. "Chưa ghi nhận" vs. a real date (see
 *    `AccountsListTable.tsx`).
 *
 * EAA-1804: buttons swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`. The issue-account drawer's four
 * raw `<label><input>` pairs (username/email/last name/first name) are now
 * `FormField` + kit `Input`, and its three unconditional
 * `portal-admin-hint` paragraphs (intro/assign/audit) — which used to stack
 * regardless of any field state — are folded into ONE `Callout` above the
 * fields, since none of the three is specific to a single field.
 */
export interface AccountsPageProps {
    /**
     * EAA-2068: lock the board to one application — the app-detail screen's
     * "Tài khoản" sub-view. Every request carries `app=<code>` (list, the
     * select-all-matching subs read, and the facet counts), the rail's
     * "Ứng dụng" group is not offered, and the board renders embedded (no page
     * header, no Tài khoản / Nhật ký tab strip — the host page owns both).
     * Every row action, bulk action and dialog is the same component tree as
     * `/console/accounts`, gated by the same `console.accounts.*` keys.
     */
    lockedApp?: { code: string; name: string };
}

export function AccountsPage({ lockedApp }: AccountsPageProps = {}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const toast = useToast();

    const canIssue = can('console.accounts.issue');
    const canDelete = can('console.accounts.delete');
    const canReset = can('console.accounts.reset');
    // EAA-1820 (frame 86): `console.accounts.lock` gates PATCH /accounts/{sub}
    // (AccountDetailView.patch — see updateAccountEnabled's docstring).
    const canLock = can('console.accounts.lock');
    const canViewAudit = can('console.audit.view');

    const [tab, setTab] = useState<'accounts' | 'audit'>('accounts');

    const listEnabled = authenticated && Boolean(accessToken) && canListAccounts(can);
    const adapter = useRealmAccountsAdapter(config.apiBase, accessToken, listEnabled, {
        lockedApp: lockedApp?.code,
    });
    // EAA-1839: presentation state only — rail quick filter, visible columns, row
    // density. Split from the adapter because none of it reaches a request; see the
    // hook's docstring for what is per-screen here and what is shared.
    const views = useRealmAccountViews();

    // EAA-1839 (2026-08-21, user request): the quick drill-in, opened from the row
    // kebab's "Xem chi tiết" (and from the row click once the shared table can
    // route them apart — see item 1 of this file's docstring).
    const [detailRow, setDetailRow] = useState<KeycloakAccountRow | null>(null);

    function handleViewDetail(row: KeycloakAccountRow) {
        navigate(`/console/accounts/${encodeURIComponent(row.sub)}`);
    }

    // EAA-1820 (frames 84/86, row kebab): "Gán Level" and "Vô hiệu hoá tài
    // khoản"/"Kích hoạt lại" — see each hook's docstring for why these are
    // realm-specific rather than shared with the org-scoped screen.
    const assignLevel = useAssignLevelToAccount(adapter.invalidate);
    const deactivate = useDeactivateAccount(adapter.invalidate);
    // EAA-1820 (frame 75, bulk bar): "Gán vào đơn vị" — grants the selected
    // identities into one đơn vị + Level at once. See the hook's docstring for
    // why this is N sequential creates and not one bulk request.
    const bulkAssignOrg = useBulkAssignAccountsToOrg(adapter.invalidate, () => adapter.setSelected(new Set()));

    // ---- issue ----
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [issued, setIssued] = useState<AccountIssued | null>(null);
    const [issueModalOpen, setIssueModalOpen] = useState(false);

    const issueMutation = useMutation({
        mutationFn: () =>
            issueAccount(config.apiBase, accessToken as string, {
                username: form.username.trim(),
                ...(form.email.trim() ? { email: form.email.trim() } : {}),
                ...(form.first_name.trim() ? { first_name: form.first_name.trim() } : {}),
                ...(form.last_name.trim() ? { last_name: form.last_name.trim() } : {}),
                credential_mode: form.credential_mode,
            }),
        onSuccess: (data) => {
            setIssueModalOpen(false);
            setIssued(data);
            setForm(EMPTY_FORM);
            setFormError(null);
            toast.success(t('console.accounts.toasts.issueSuccess', { username: data.username }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.issueErrorFallback');
            setFormError(message);
            toast.error(t('console.accounts.toasts.issueError', { username: form.username.trim() }));
        },
    });

    // ---- single delete ----
    const [deleteTarget, setDeleteTarget] = useState<KeycloakAccountRow | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const deleteMutation = useMutation({
        mutationFn: (sub: string) => deleteAccount(config.apiBase, accessToken as string, sub),
        onSuccess: (_data, sub) => {
            setDeleteTarget(null);
            setDeleteError(null);
            adapter.setSelected((prev) => {
                const next = new Set(prev);
                next.delete(sub);
                return next;
            });
            toast.success(t('console.accounts.toasts.deleteSuccess', { sub }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.deleteErrorFallback');
            setDeleteError(message);
            toast.error(t('console.accounts.toasts.deleteError', { sub: deleteTarget?.sub ?? '' }));
        },
    });

    function openDeleteFor(row: KeycloakAccountRow) {
        setDeleteTarget(row);
        setDeleteError(null);
    }

    // ---- profile lock (frame 80) ----
    const [lockTarget, setLockTarget] = useState<KeycloakAccountRow | null>(null);
    const [lockError, setLockError] = useState<string | null>(null);

    const lockMutation = useMutation({
        mutationFn: ({ sub, endSessions }: { sub: string; username: string; endSessions: boolean }) =>
            updateAccountEnabled(config.apiBase, accessToken as string, sub, false, { endSessions }),
        onSuccess: (_data, { username }) => {
            setLockTarget(null);
            setLockError(null);
            toast.success(t('console.accounts.lockModal.success', { username }));
            adapter.invalidate();
        },
        onError: (error) => {
            setLockError(error instanceof OrgAdminApiError ? error.message : t('console.accounts.deactivate.errorFallback'));
        },
    });

    const [resetIdent, setResetIdent] = useState('');
    const [resetTarget, setResetTarget] = useState<KeycloakAccountRow | null>(null);
    const [resetResult, setResetResult] = useState<AccountPasswordReset | null>(null);
    const [resetError, setResetError] = useState<string | null>(null);
    const [resetModalOpen, setResetModalOpen] = useState(false);

    // EAA-1779: the credential choice (generated temp vs. an admin-typed
    // password) for the single-account drawer. Shares its rules with the bulk
    // dialog's own instance below via `useResetCredential`.
    const resetCredential = useResetCredential();

    const resetMutation = useMutation({
        mutationFn: ({ ident, endSessions }: { ident: string; endSessions: boolean }) => {
            const trimmed = ident.trim();
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
            const identity = isUuid ? { sub: trimmed } : { username: trimmed };
            return resetAccountPassword(config.apiBase, accessToken as string, {
                ...identity,
                end_sessions: endSessions,
                ...resetCredential.toRequest(),
            });
        },
        onSuccess: (data) => {
            setResetModalOpen(false);
            // EAA-1779: only the generated mode produces a secret to reveal. In
            // admin-set mode the operator already holds the value, so opening the
            // one-time panel would show an empty box.
            setResetResult(data.mode === 'admin_set' ? null : data);
            setResetIdent('');
            setResetError(null);
            resetCredential.clear();
            toast.success(t('console.accounts.toasts.resetSuccess', { username: data.username }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.resetErrorFallback');
            setResetError(message);
            toast.error(t('console.accounts.toasts.resetError', { ident: resetIdent.trim() }));
        },
    });

    function openResetFor(row: KeycloakAccountRow) {
        setResetTarget(row);
        setResetIdent(row.sub);
        setResetError(null);
        setResetModalOpen(true);
    }

    // ---- bulk delete ----
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [bulkDeletePending, setBulkDeletePending] = useState(false);

    function openBulkDelete() {
        if (adapter.selected.size === 1) {
            const sub = [...adapter.selected][0];
            const row = adapter.rows.find((r) => r.sub === sub);
            if (row) {
                openDeleteFor(row);
            }
            return;
        }
        setBulkDeleteModalOpen(true);
    }

    async function runBulkDelete() {
        setBulkDeletePending(true);
        const subs = [...adapter.selected];
        const results = await Promise.allSettled(subs.map((sub) => deleteAccount(config.apiBase, accessToken as string, sub)));
        const failed = subs.filter((_, i) => results[i].status === 'rejected');
        setBulkDeletePending(false);
        setBulkDeleteModalOpen(false);
        adapter.setSelected(new Set(failed));
        if (failed.length === 0) {
            toast.success(t('console.accounts.toasts.bulkDeleteSuccess', { count: subs.length }));
        } else {
            toast.error(
                t('console.accounts.toasts.bulkDeletePartial', {
                    succeeded: subs.length - failed.length,
                    total: subs.length,
                    failed: failed.length,
                })
            );
        }
        adapter.invalidate();
    }

    // ---- bulk reset ----
    const [bulkResetConfirmOpen, setBulkResetConfirmOpen] = useState(false);
    const [bulkResetPending, setBulkResetPending] = useState(false);
    const [bulkResetResults, setBulkResetResults] = useState<BulkResetResultRow[] | null>(null);
    const [bulkResetMode, setBulkResetMode] = useState<ResetCredentialMode>('temp_password');
    // EAA-1779: its own credential state, separate from the drawer's — the two
    // dialogs can be opened one after the other and a password typed for a
    // single account must not leak into a bulk run over six.
    const bulkCredential = useResetCredential();

    // EAA-1829: the server accepts at most this many subs per bulk reset request.
    const BULK_RESET_CHUNK = 200;
    const [bulkResetProgress, setBulkResetProgress] = useState<{ done: number; total: number } | null>(null);

    async function runBulkReset() {
        setBulkResetPending(true);
        // EAA-1829: the selection may span pages ("Chọn tất cả"), so it is the set of
        // subs that drives the run, not the rows on screen.
        const subs = [...adapter.selected];
        // EAA-1779: ONE request per chunk instead of one per row. This used to be a
        // Promise.allSettled over one `resetAccountPassword` per row — 100 rows meant
        // 100 concurrent requests, each holding a server-side database connection
        // (the dev PostgreSQL slot-exhaustion incident). The server now fans out to
        // Keycloak itself and still audits and reports every row on its own. Chunks
        // run one after another so a 4,700-account selection is 24 sequential
        // requests with a progress readout, never 24 concurrent ones.
        const credentialBody = bulkCredential.toRequest();
        const results: BulkResetResultRow[] = [];
        setBulkResetProgress({ done: 0, total: subs.length });
        for (const part of chunk(subs, BULK_RESET_CHUNK)) {
            try {
                const outcome = await bulkResetAccountPasswords(config.apiBase, accessToken as string, {
                    subs: part,
                    ...credentialBody,
                });
                for (const r of outcome.results) {
                    results.push({
                        sub: r.sub,
                        username: r.username || adapter.usernameFor(r.sub),
                        temp_password: r.temp_password ?? undefined,
                        ok: r.ok,
                        error: r.error ?? undefined,
                    });
                }
            } catch (error) {
                // Nothing in this chunk ran (validation, realm, auth): every row of it
                // is a failure with the same message and stays selected for a retry.
                const message =
                    error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.unknownError');
                for (const sub of part) {
                    results.push({ sub, username: adapter.usernameFor(sub), ok: false, error: message });
                }
            }
            setBulkResetProgress({ done: Math.min(subs.length, results.length), total: subs.length });
        }
        setBulkResetProgress(null);
        setBulkResetPending(false);
        setBulkResetConfirmOpen(false);
        // Captured BEFORE clearing, so the result panel renders the mode the run
        // actually used rather than the freshly-reset default.
        setBulkResetMode(bulkCredential.mode);
        bulkCredential.clear();
        setBulkResetResults(results);
        adapter.setSelected(new Set(results.filter((r) => !r.ok).map((r) => r.sub)));
        adapter.invalidate();
    }

    if (!listEnabled) {
        return null;
    }

    const hasEmail = Boolean(form.email.trim());

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!form.username.trim()) {
            setFormError(t('console.accounts.validation.usernameRequired'));
            return;
        }
        if (form.credential_mode === 'invite' && !hasEmail) {
            setFormError(t('console.accounts.validation.inviteNeedsEmail'));
            return;
        }
        issueMutation.mutate();
    };

    const closeIssueModal = () => {
        setIssueModalOpen(false);
        setFormError(null);
    };
    const closeResetDrawer = () => {
        setResetModalOpen(false);
        setResetTarget(null);
        setResetError(null);
    };

    const showEmptyNew = adapter.listQuery.isSuccess && adapter.rows.length === 0 && !adapter.hasActiveFilters;
    const showEmptyFiltered = adapter.listQuery.isSuccess && adapter.rows.length === 0 && adapter.hasActiveFilters;

    // EAA-1829: "Chọn tất cả" can tick rows that are not on this page. Only the reset
    // has a server-side bulk endpoint; delete and assign-org still act row by row, so
    // they stay page-bound rather than firing thousands of single requests.
    const selectionSpansPages = [...adapter.selected].some((sub) => !adapter.rows.some((r) => r.sub === sub));
    const pageOnlyReason = selectionSpansPages ? t('console.accounts.bulkBar.pageOnlyAction') : undefined;
    const bulkActions: AccountsBulkAction[] = [
        // EAA-1820 (frame 75): "Gán vào đơn vị" — opens the bulk org/Level
        // dialog over exactly the rows currently ticked. Gated the same coarse
        // way as the row-level "Gán Level" kebab item (`canIssue`) since it is
        // the same underlying `createOrgAssignment` write, re-checked per org
        // server-side.
        ...(canIssue
            ? [
                  {
                      key: 'assign-org',
                      label: t('console.accounts.bulkBar.assignOrgAction'),
                      disabled: selectionSpansPages,
                      disabledReason: pageOnlyReason,
                      onClick: () => bulkAssignOrg.open(adapter.rows.filter((r) => adapter.selected.has(r.sub))),
                  },
              ]
            : []),
        ...(canReset
            ? [{ key: 'reset', label: t('console.accounts.bulkBar.resetAction'), onClick: () => setBulkResetConfirmOpen(true) }]
            : []),
        ...(canDelete
            ? [
                  {
                      key: 'delete',
                      label: t('console.accounts.bulkBar.deleteAction'),
                      danger: true,
                      disabled: selectionSpansPages,
                      disabledReason: pageOnlyReason,
                      onClick: openBulkDelete,
                  },
              ]
            : []),
    ];

    let body: React.ReactNode;
    if (adapter.listQuery.isLoading) {
        body = (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">{t('common.loading')}</span>
                <SkeletonTable rows={6} columns={ACCOUNTS_TABLE_COLUMNS} />
            </div>
        );
    } else if (adapter.listQuery.isError) {
        body = (
            <p className="portal-admin-error">
                {t('console.accounts.page.loadError')}{' '}
                <Button onClick={() => adapter.listQuery.refetch()}>
                    {t('console.accounts.page.retryButton')}
                </Button>
            </p>
        );
    } else if (showEmptyNew) {
        body = <AccountsEmptyNew canIssue={canIssue} onIssue={() => setIssueModalOpen(true)} />;
    } else if (showEmptyFiltered) {
        body = <AccountsEmptyFiltered search={adapter.search} onClear={adapter.clearFilters} />;
    } else {
        body = (
            <>
                <AccountsBulkBar
                    selectedCount={adapter.selected.size}
                    /* EAA-1829: cross-page selection over the current filters, served by
                       one `GET /accounts/subs` request. */
                    selectAllMatching={{
                        totalCount: adapter.total,
                        onSelectAll: () => void adapter.selectAllMatching(),
                        busy: adapter.selectAllBusy,
                        allSelected: adapter.selected.size >= adapter.total,
                    }}
                    actions={bulkActions}
                    onClear={() => adapter.setSelected(new Set())}
                    busy={bulkDeletePending || bulkResetPending}
                    notes={adapter.selectAllCapped ? [t('console.accounts.bulkBar.selectAllCapped')] : undefined}
                />
                <AccountsListTable
                    rows={adapter.rows}
                    selected={adapter.selected}
                    onSelectedChange={adapter.setSelected}
                    sortField={adapter.sortField}
                    sortDirection={adapter.sortDirection}
                    onSort={adapter.onSort}
                    onViewDetail={handleViewDetail}
                    onOpenDetailModal={setDetailRow}
                    views={views}
                    onResetOne={openResetFor}
                    onDeleteOne={openDeleteFor}
                    onAssignLevel={assignLevel.open}
                    onDeactivate={deactivate.open}
                    canReset={canReset}
                    canDelete={canDelete}
                    canAssignLevel={canIssue}
                    canLock={canLock}
                    lastLoginAvailable={adapter.lastLoginAvailable}
                />
                <AccountsPagination
                    page={adapter.page}
                    totalPages={adapter.totalPages}
                    pageSize={adapter.pageSize}
                    onPageSizeChange={adapter.setPageSize}
                    rangeFrom={adapter.rangeFrom}
                    rangeTo={adapter.rangeTo}
                    total={adapter.total}
                    onGoTo={adapter.setPage}
                    pageSizeOptions={adapter.pageSizeOptions}
                />
            </>
        );
    }

    return (
        <>
            {/* EAA-1839: `AccountsScreen` is generic over its tab union since frame 35
                needed four tabs. The realm screen still has exactly two, and the type
                argument is explicit because `onTabChange={setTab}` is a
                `Dispatch<SetStateAction<…>>`, whose parameter type would otherwise
                widen the inferred union to `string`. */}
            <AccountsScreen<'accounts' | 'audit'>
                context="realm"
                embedded={Boolean(lockedApp)}
                title={t('console.accounts.page.title')}
                subtitle={''}
                headerActions={
                    canIssue && (
                        <Button type="primary" onClick={() => setIssueModalOpen(true)}>
                            {t('console.accounts.page.createButton')}
                        </Button>
                    )
                }
                tab={tab}
                onTabChange={setTab}
                showAuditTab={canViewAudit && !lockedApp}
                auditPanel={lockedApp ? null : <AccountAuditPanel />}
                facetGroups={adapter.facetGroups}
                onSelectFacet={adapter.selectFacet}
                hasActiveFilters={adapter.hasActiveFilters}
                onClearFilters={adapter.clearFilters}
                search={adapter.search}
                onSearchChange={adapter.setSearch}
                railQuickFilter={{ value: views.railQuery, onChange: views.setRailQuery }}
                railNote={t('console.accounts.facetRail.cumulativeNote')}
                activeFilterCount={adapter.activeFilterCount}
                /* EAA-1839 (2026-08-24, user request): "VỊ TRÍ BỘ LỌC" — the third
                   section of the "Hiển thị" popover, alongside "CỘT" and "MẬT ĐỘ
                   DÒNG", matching the org board. This is a TOP-LEVEL prop rather than
                   part of `toolbarExtras` because `AccountsScreen` needs the value
                   itself (it switches the board between the 236px rail column and the
                   collapsed "Bộ lọc (N)" popover) and forwards it to the toolbar
                   itself. The previous pass deliberately withheld it — see
                   `useRealmAccountViews`'s docstring for why that refusal no longer
                   holds. */
                placement={{ value: views.placement, onChange: views.setPlacement }}
                /* EAA-1839: the rest of the "Hiển thị" popover (column visibility + row
                   density) and the refresh glyph. */
                toolbarExtras={{
                    columns: { items: views.columnItems, onToggle: views.toggleColumn },
                    density: { value: views.density, onChange: views.setDensity },
                    onRefresh: adapter.invalidate,
                    refreshBusy: adapter.listQuery.isFetching,
                }}
                body={body}
            />

            <OneTimeSecretModal
                secret={
                    issued
                        ? { kind: 'created', username: issued.username, sub: issued.sub, tempPassword: issued.temp_password }
                        : resetResult
                          ? {
                                kind: 'reset',
                                username: resetResult.username,
                                tempPassword: resetResult.temp_password ?? undefined,
                            }
                          : null
                }
                onClose={() => {
                    setIssued(null);
                    setResetResult(null);
                }}
            />

            {/* EAA-1779: a dialog, not an inline block. The page is `portal-console-page--fit`
                (fills the shell, clips overflow), so a 100-row panel appended after it
                painted over the toolbar and off the bottom of the viewport. */}
            <Modal
                className="acc-bulk-result-modal"
                open={bulkResetResults !== null}
                onCancel={() => setBulkResetResults(null)}
                title={t('console.accounts.bulkResetResult.modalTitle')}
                width={640}
                footer={null}
                destroyOnClose
            >
                {bulkResetResults && (
                    <BulkResetResultPanel
                        results={bulkResetResults}
                        mode={bulkResetMode}
                        onDismiss={() => setBulkResetResults(null)}
                    />
                )}
            </Modal>

            <AccountDetailModal
                row={detailRow}
                onClose={() => setDetailRow(null)}
                canReset={canReset}
                canDelete={canDelete}
                canLock={canLock}
                canViewAudit={canViewAudit}
                /* EAA-1839: each action CLOSES the modal before opening its own
                   dialog — two stacked overlays would leave the operator unsure
                   which one Escape dismisses, and every one of these dialogs is
                   already reachable from the row kebab on its own. */
                onReset={(row) => {
                    setDetailRow(null);
                    openResetFor(row);
                }}
                onDelete={(row) => {
                    setDetailRow(null);
                    openDeleteFor(row);
                }}
                onLock={(row) => {
                    setDetailRow(null);
                    setLockTarget(row);
                }}
            />

            <AssignLevelToAccountDialog vm={assignLevel} />
            <DeactivateAccountDialog vm={deactivate} />
            <BulkAssignAccountsToOrgDialog vm={bulkAssignOrg} />
            {bulkAssignOrg.results && (
                <div className="acc-detail-line">
                    <BulkAssignOrgResultPanel results={bulkAssignOrg.results} onDismiss={bulkAssignOrg.dismissResults} />
                </div>
            )}

            {canLock && (
                <LockAccountDialog
                    row={lockTarget}
                    pending={lockMutation.isPending}
                    error={lockError}
                    onClose={() => setLockTarget(null)}
                    onConfirm={(endSessions) =>
                        lockTarget &&
                        lockMutation.mutate({ sub: lockTarget.sub, username: lockTarget.username, endSessions })
                    }
                />
            )}

            {canDelete && (
                <DeleteAccountDialog
                    row={deleteTarget}
                    pending={deleteMutation.isPending}
                    error={deleteError}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={(sub) => deleteMutation.mutate(sub)}
                />
            )}

            {canDelete && (
                <BulkDeleteConfirmModal
                    open={bulkDeleteModalOpen}
                    count={adapter.selected.size}
                    pending={bulkDeletePending}
                    onCancel={() => setBulkDeleteModalOpen(false)}
                    onConfirm={runBulkDelete}
                />
            )}

            {canReset && (
                <Modal
                    open={bulkResetConfirmOpen}
                    onCancel={() => setBulkResetConfirmOpen(false)}
                    title={t('console.accounts.bulkResetModal.title')}
                    footer={
                        <>
                            <Button onClick={() => setBulkResetConfirmOpen(false)} disabled={bulkResetPending}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={runBulkReset}
                                disabled={bulkResetPending || !bulkCredential.valid}
                            >
                                {bulkResetPending
                                    ? bulkResetProgress
                                        ? t('console.accounts.bulkResetModal.progress', bulkResetProgress)
                                        : t('console.accounts.bulkResetModal.pending')
                                    : t('console.accounts.bulkResetModal.submit', { count: adapter.selected.size })}
                            </Button>
                        </>
                    }
                >
                    <p className="portal-admin-hint">
                        {bulkCredential.mode === 'admin_set'
                            ? t('console.accounts.bulkResetModal.hintAdminSet', { count: adapter.selected.size })
                            : t('console.accounts.bulkResetModal.hint', { count: adapter.selected.size })}
                    </p>
                    <ResetCredentialFields credential={bulkCredential} disabled={bulkResetPending} />
                    {/* EAA-1779: the "save it now, it is shown once" warning only
                        applies to the generated mode — an admin-typed password is
                        never revealed back, so there is nothing to write down. */}
                    {bulkCredential.mode !== 'admin_set' && (
                        <p className="portal-accounts-onetime-warning">
                            {t('console.accounts.bulkResetModal.onetimeWarning')}
                        </p>
                    )}
                </Modal>
            )}

            {canIssue && (
                <Drawer
                    open={issueModalOpen}
                    onClose={closeIssueModal}
                    title={t('console.accounts.issueDrawer.title')}
                    width={520}
                    footer={
                        <>
                            <Button onClick={closeIssueModal} disabled={issueMutation.isPending}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form="accounts-issue-form"
                                disabled={issueMutation.isPending}
                            >
                                {issueMutation.isPending
                                    ? t('console.accounts.issueDrawer.pending')
                                    : t('console.accounts.issueDrawer.title')}
                            </Button>
                        </>
                    }
                >
                    <form id="accounts-issue-form" className="portal-admin-form" onSubmit={onSubmit}>
                        {/* EAA-1804: the three unconditional hints (intro/assign/audit)
                            used to render as three separate `portal-admin-hint`
                            paragraphs at the top and bottom of this form — none tied
                            to one field, so a single `Callout` above the fields says
                            all three without the stack. */}
                        <Callout tone="info">
                            {t('console.accounts.issueDrawer.introHint')}{' '}
                            {t('console.accounts.issueDrawer.assignHint')}{' '}
                            {t('console.accounts.issueDrawer.auditHint')}
                        </Callout>
                        <FormField label={t('console.accounts.issueDrawer.usernameLabel')}>
                            <Input
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value })}
                                placeholder={t('console.accounts.issueDrawer.usernamePlaceholder')}
                                required
                            />
                        </FormField>
                        <FormField label={t('console.accounts.issueDrawer.emailLabel')}>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => {
                                    const email = e.target.value;
                                    setForm((prev) => ({
                                        ...prev,
                                        email,
                                        credential_mode: email.trim() ? prev.credential_mode : 'temp_password',
                                    }));
                                }}
                                placeholder={t('console.accounts.issueDrawer.emailPlaceholder')}
                            />
                        </FormField>
                        <FormField label={t('console.accounts.issueDrawer.lastNameLabel')}>
                            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                        </FormField>
                        <FormField label={t('console.accounts.issueDrawer.firstNameLabel')}>
                            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                        </FormField>
                        <fieldset className="portal-console-radio-group acc-cred-modes">
                            <legend>{t('console.accounts.issueDrawer.credModeLegend')}</legend>
                            <OptionCard
                                inputType="radio"
                                name="credential_mode"
                                selected={form.credential_mode === 'invite'}
                                disabled={!hasEmail}
                                onSelect={() => setForm({ ...form, credential_mode: 'invite' })}
                                title={t('console.accounts.issueDrawer.inviteModeLabel')}
                                description={
                                    <>
                                        {t('console.accounts.issueDrawer.inviteModeDesc')}
                                        {!hasEmail &&
                                            ` ${t('console.accounts.issueDrawer.inviteModeNeedsEmailSuffix')}`}
                                    </>
                                }
                            />
                            <OptionCard
                                inputType="radio"
                                name="credential_mode"
                                selected={form.credential_mode === 'temp_password'}
                                onSelect={() => setForm({ ...form, credential_mode: 'temp_password' })}
                                title={t('console.accounts.issueDrawer.tempPasswordModeLabel')}
                                description={t('console.accounts.issueDrawer.tempPasswordModeDesc')}
                            />
                        </fieldset>
                        {formError && <p className="portal-admin-error">{formError}</p>}
                    </form>
                </Drawer>
            )}

            {canReset && (
                <ResetAccountDrawer
                    open={resetModalOpen}
                    target={resetTarget}
                    ident={resetIdent}
                    credential={resetCredential}
                    pending={resetMutation.isPending}
                    error={resetError}
                    onIdentChange={setResetIdent}
                    onClose={closeResetDrawer}
                    onSubmit={({ endSessions }) => {
                        if (!resetIdent.trim()) {
                            setResetError(t('console.accounts.validation.resetIdentRequired'));
                            return;
                        }
                        resetMutation.mutate({ ident: resetIdent, endSessions });
                    }}
                />
            )}
        </>
    );
}

export default AccountsPage;
